import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ExtendedContentInsertVariable } from 'api/graphQL/types/contentInsert.types'
import { useSelectedKey } from 'components/WorkspaceEditor/NodeKeySelectionStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    findRootArticleNode,
    getContentOwnerType,
    getParentKeyChain,
} from 'components/WorkspaceEditor/utils'
import {
    useShallowSelector,
    useTextContentElementToAdd,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { PRODUCT_TYPE_PROPERTY_NODE_TYPE } from '../../../features/bestPractices/constants'
import { findContentItemDepth } from '../../../features/promoteDemote/utils/depthCalculator'
import { useCopyPasteShallowSelector } from '../store/CopyPasteStore'

interface CKEditorPasteEvent {
    stop: () => void
}

interface CKEditorPasteData {
    dataTransfer: {
        getData: (format: string) => string
        types?: string[]
    }
}

const HTML_DETECTION = {
    PARAGRAPH_TAG: '<p',
} as const

/**
 * Sanitizes and decodes HTML content, especially from Microsoft Word.
 * Handles fragment extraction, entity decoding, and removes Word-specific formatting.
 */
const decodeHtmlEntities = (html: string): string => {
    if (!html) return html

    let processedHtml = html

    // Extract Word fragment content if present
    const fragmentMatch = processedHtml.match(
        /<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/
    )
    if (fragmentMatch) {
        processedHtml = fragmentMatch[1]
    }

    // Use browser's built-in HTML entity decoding
    const textarea = document.createElement('textarea')
    textarea.innerHTML = processedHtml
    processedHtml = textarea.value

    return (
        processedHtml
            // Remove MSO conditional comments
            .replace(/<!\[if !supportLists\]>[\s\S]*?<!\[endif\]>/g, '')
            // Remove MSO-specific tags
            .replace(/<\/?o:p[^>]*>|<a\s+name="[^"]*"[^>]*>|<\/a>/g, '')
            // Clean up Word list numbering patterns
            .replace(
                /(^|>)\s*(?:[A-Za-z]\.|\d+\.|[ivxlcdm]+\.|[•\*\-])\s+/gi,
                '$1'
            )
    )
}

export const useHtmlParagraphPaste = () => {
    const { projectId } = useParams<{
        projectId: string
    }>()
    const {
        contentItemViewNodes,
        selectedProjectElementNode,
        treeData,
        previousContentItemViewNodes,
        setContentItemViewNodes,
        setTextContentElementToAdd,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        selectedProjectElementNode: state.selectedProjectElementNode,
        treeData: state.treeData,
        previousContentItemViewNodes: state.previousContentItemViewNodes,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
        setTextContentElementToAdd: state.actions.setTextContentElementToAdd,
    }))

    const selectedKey = useSelectedKey()
    const productTypeNuggetId = useMemo(() => {
        const isProductType =
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTTYPE
        return isProductType
            ? selectedProjectElementNode?.elementId ??
                  selectedProjectElementNode?.baseElementId
            : null
    }, [selectedProjectElementNode])

    const { openPasteDialog } = useCopyPasteShallowSelector((state) => ({
        openPasteDialog: state.openPasteDialog,
    }))

    const textContentElementToAdd = useTextContentElementToAdd()

    const getStartingLevel = (
        node: Partial<ContentItemViewNodesType>
    ): number => {
        if (node.id && treeData) {
            const hierarchicalDepth = findContentItemDepth(node.id, treeData)
            if (hierarchicalDepth >= 0) {
                return hierarchicalDepth + 1
            }
        }

        // Default fallback: start at level 1 for top-level content
        return 1
    }

    const getPasteIndex = (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[],
        node: Partial<ContentItemViewNodesType>
    ): number => {
        // If pasting into an existing paragraph, use order + 1 to place content after it
        if (node.order !== undefined) {
            return node.order + 1
        }

        // Fallback: Find all siblings and insert at the end
        const siblings = contentItemViewNodes
            .filter(
                (ci) =>
                    ci.partType === node.partType &&
                    ci.textType === node.textType &&
                    ci.dtvParentId === node.dtvParentId
            )
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

        return siblings.length
    }

    const handleHtmlParagraphPaste = useCallback(
        ({
            rootArticleContentItemIds,
            ...rest
        }: ExtendedContentInsertVariable) => {
            openPasteDialog({
                input: {
                    ...rest,
                    projectId,
                    productTypeNuggetId: productTypeNuggetId,
                },
                partType: rest.partType,
                productFamilyId: rest.productFamilyId,
                rootArticleContentItemIds: rootArticleContentItemIds ?? [],
            })
        },
        [openPasteDialog, projectId, productTypeNuggetId]
    )

    const handleHtmlParagraphPasteFromInput = useCallback(
        (pastedHtml: string) => {
            const node = contentItemViewNodes.find((n) => n.id === selectedKey)
            if (!node || !node.elements) return

            const rootArticleId = findRootArticleNode(
                contentItemViewNodes,
                node.id ?? ''
            )?.id

            const parentNode = contentItemViewNodes?.find((item) =>
                node.dtvContentItemParentId
                    ? item.id === node.dtvContentItemParentId
                    : item.dtvItemId === node.dtvParentId
            )

            const isProductTypeProperty =
                node.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE ||
                parentNode?.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE

            const ownerConfig = isProductTypeProperty
                ? {
                      contentItemOwnerType: 'ProductTypeProperty',
                      contentItemOwnerId: null,
                      contentItemParentType: 'ContentItem',
                  }
                : {
                      contentItemOwnerType: getContentOwnerType(
                          parentNode,
                          node
                      ),
                      contentItemOwnerId:
                          node.propertyRefId || node.dtvItemId || null,
                      contentItemParentType: parentNode?.type ?? 'None',
                  }

            handleHtmlParagraphPaste?.({
                html: pastedHtml,
                index: getPasteIndex(contentItemViewNodes, node),
                contentItemParentId:
                    node.dtvContentItemParentId || node.dtvParentId || null,
                ...ownerConfig,
                productFamilyId: node.productFamilyId ?? '',
                productTypePropertyType: node.isSharedProductTypeProperty
                    ? 'Shared'
                    : 'Specific',
                startingLevel: getStartingLevel(node),
                partType: node.partType?.toUpperCase() ?? '',
                rootArticleContentItemIds: rootArticleId ? [rootArticleId] : [],
                parentPropertyKey:
                    getParentKeyChain(contentItemViewNodes, node) || null,
            })
        },
        [handleHtmlParagraphPaste, contentItemViewNodes, selectedKey, treeData]
    )

    /**
     * Handles CKEditor paste events for HTML paragraphs.
     * - If adding new content and the paste contains HTML or multiple lines, process it and prevent the default paste.
     * - Otherwise, prevent the default paste for HTML or multi-line content.
     *
     * @param evt - CKEditor paste event
     * @param data - Clipboard data from the paste event
     */
    const handlePaste = useCallback(
        (evt: CKEditorPasteEvent, data: CKEditorPasteData) => {
            const pastedHtml = data.dataTransfer.getData('text/html')
            const pastedText = data.dataTransfer.getData('text/plain')

            const isMultiLineContent =
                pastedText &&
                (pastedText.includes('\n') || pastedText.includes('\r'))
            const hasRichFormatting =
                pastedHtml &&
                pastedHtml.indexOf(HTML_DETECTION.PARAGRAPH_TAG) !== -1 &&
                data.dataTransfer.types?.some(
                    (type: string) => type?.toLowerCase().trim() === 'text/html'
                )

            // Only intercept paste for multi-line content or rich formatting
            if (!(isMultiLineContent || hasRichFormatting)) return

            // Prevent paste for rich formatting when editing existing content
            if (hasRichFormatting && !textContentElementToAdd) {
                evt.stop()
                return
            }

            evt.stop()

            // Remove the empty container that was optimistically added to prevent it from showing up after paste
            if (textContentElementToAdd && previousContentItemViewNodes) {
                setContentItemViewNodes(previousContentItemViewNodes)
                setTextContentElementToAdd(null)
            }

            // Decode HTML entities while preserving HTML structure
            const decodedContent = pastedHtml
                ? decodeHtmlEntities(pastedHtml)
                : pastedText
            handleHtmlParagraphPasteFromInput(decodedContent)
        },
        [
            handleHtmlParagraphPasteFromInput,
            textContentElementToAdd,
            previousContentItemViewNodes,
            setContentItemViewNodes,
            setTextContentElementToAdd,
        ]
    )

    return {
        handlePaste,
    }
}
